import React, { useContext } from 'react';
import { Island, Panel, PanelItemGroup, useSubscription } from '@rta/rta-blueprint-library';

import { AppContext } from '@r-app/app-context';
import { getIslandAddonState, ISLAND_ADDONS } from '../blueprint-view/helpers/island-addons';
import { renderTracking } from '@r-services/render-tracking';
import { transformToGlobal } from '../helpers/sku-labels';

export type RenderUtilityLinesOverheadProps = {
	islands: Array<Island>;
};

// Matches the owner's #466 mockup: a red dot on the appliance's back-LEFT corner and a
// blue dot on the back-RIGHT corner — candidate stub-up points, no labels, no stems.
// The locating dimensions already exist in the MFG overhead views.
const LEFT_DOT_COLOR = '#d11a1a';
const RIGHT_DOT_COLOR = '#1a4fd1';
const DOT_RADIUS = 1.4;

type StubDot = {
	id: string;
	x: number;
	z: number;
	color: string;
};

/**
 * Utility stub-up layer (#466): for every island whose `utilityLines` add-on is on, each
 * fuelled appliance gets two corner dots marking its candidate utility entry points.
 * Renders in GLOBAL overhead coordinates as a sibling of the islands.
 */
export const RenderUtilityLinesOverhead = (props: RenderUtilityLinesOverheadProps): React.ReactElement => {
	const { islands } = props;
	const { controller } = useContext(AppContext);
	const { kitchen } = controller;

	useSubscription(kitchen, false, [['kitchen', 'updated'], ['panel_item', '*'], ['panel_item_group', '*'], ['island', '*']]);

	renderTracking.incrementRender('RenderUtilityLinesOverhead');

	// Parked as coming soon (#466): dots stay off until the routing design lands.
	if (ISLAND_ADDONS.find(({ key }) => key === 'utilityLines')?.comingSoon) return <g className="utility-lines-layer" />;

	const dots: Array<StubDot> = [];

	islands
		.filter((island) => getIslandAddonState(kitchen, island, 'utilityLines'))
		.forEach((island) => {
			const { x: islandX, z: islandZ } = island.center;
			const rotation = island.axis?.y ?? 0;

			island.panels.children.forEach((panel: Panel) => {
				panel.panelItemGroups.children.forEach((group: PanelItemGroup) => {
					group.items.children.filter((panelItem) => panelItem.item.fuel).forEach((panelItem) => {
						const cx = panelItem.center.x;
						const cz = panelItem.center.z;
						const halfW = panelItem.size.width / 2;
						const halfD = panelItem.size.depth / 2;

						// The stub corners sit on the footprint edge nearer the island centerline
						// (the appliance's back). Rotated placements are not handled yet — fuelled
						// appliances live on straight panels today.
						const backZ = Math.abs(cz - halfD) <= Math.abs(cz + halfD) ? cz - halfD : cz + halfD;

						const left = transformToGlobal(cx - halfW, backZ, islandX, islandZ, rotation);
						const right = transformToGlobal(cx + halfW, backZ, islandX, islandZ, rotation);
						dots.push({ id: `${panelItem.id}-l`, x: left.x, z: left.z, color: LEFT_DOT_COLOR });
						dots.push({ id: `${panelItem.id}-r`, x: right.x, z: right.z, color: RIGHT_DOT_COLOR });
					});
				});
			});
		});

	return (
		<g className="utility-lines-layer">
			{dots.map(({ id, x, z, color }) => (
				<circle key={`utility-${id}`} cx={x} cy={z} r={DOT_RADIUS} fill={color} stroke="#ffffff" strokeWidth={0.25} />
			))}
		</g>
	);
};
